import type { RouteRecordRaw } from 'vue-router';
import { isNavigationFailure, Router } from 'vue-router';
import { useUserStoreWidthOut } from '@/store/modules/user';
import { useAsyncRouteStoreWidthOut } from '@/store/modules/asyncRoute';
import { ACCESS_TOKEN } from '@/store/mutation-types';
import { storage } from '@/utils/Storage';
import { PageEnum } from '@/enums/pageEnum';
import { ErrorPageRoute } from '@/router/base';
import { useLoadingBar } from '@/hooks/web/useLoadingBar';
import { useDialog } from '@/hooks/web/useDialog';
import { CURRENT_USER, CURRENT_PROJECT } from '@/store/mutation-types';

const LOGIN_PATH = PageEnum.BASE_LOGIN;

const whitePathList = [LOGIN_PATH]; // no redirect whitelist

const loadingBar = useLoadingBar();
const dialog: any = useDialog();

export function createRouterGuards(router: Router) {
  const userStore = useUserStoreWidthOut();
  const asyncRouteStore = useAsyncRouteStoreWidthOut();
  router.beforeEach(async (to, from, next) => {
    loadingBar.start();
    if (from.path === LOGIN_PATH && to.name === 'errorPage') {
      next(PageEnum.BASE_HOME);
      return;
    }

    // Whitelist can be directly entered
    if (whitePathList.includes(to.path as PageEnum)) {
      next();
      return;
    }

    const token = storage.get(ACCESS_TOKEN);

    if (!token) {
      // You can access without permissions. You need to set the routing meta.ignoreAuth to true
      if (to.meta.ignoreAuth) {
        next();
        return;
      }
      // redirect login page
      const redirectData: { path: string; replace: boolean; query?: Recordable<string> } = {
        path: LOGIN_PATH,
        replace: true,
      };
      if (to.path) {
        redirectData.query = {
          ...redirectData.query,
          redirect: to.path,
        };
      }
      next(redirectData);
      return;
    }

    if (asyncRouteStore.getIsDynamicAddedRoute) {
      next();
      return;
    }

    // main.ts awaits router.isReady() before mounting the app, so this guard must ALWAYS
    // settle the navigation. If it throws, the app never mounts and the user is left
    // looking at the static loading spinner in index.html with no error on screen - that
    // is what a revoked default project used to do. Send them back to login instead.
    let userInfo;
    let routes;
    try {
      userInfo = await userStore.GetInfo();
      routes = await asyncRouteStore.generateRoutes(userInfo);
    } catch (error: any) {
      loadingBar.error();
      // HTTP failures already raised their own dialog in checkStatus - only the errors we
      // raise ourselves need one here, or the user gets two stacked dialogs.
      if (error?.showDialog) {
        dialog.error({
          title: 'Unable to sign in',
          content: error.message,
          positiveText: 'Close',
          maskClosable: false,
          closable: true,
        });
      }
      storage.remove(ACCESS_TOKEN);
      storage.remove(CURRENT_USER);
      sessionStorage.removeItem(CURRENT_PROJECT);
      next({ path: LOGIN_PATH, replace: true });
      return;
    }

    // 动态添加可访问路由表
    routes.forEach((item) => {
      router.addRoute(item as unknown as RouteRecordRaw);
    });

    //添加404
    const isErrorPage = router.getRoutes().findIndex((item) => item.name === ErrorPageRoute.name);
    if (isErrorPage === -1) {
      router.addRoute(ErrorPageRoute as unknown as RouteRecordRaw);
    }

    const redirectPath = (from.query.redirect || to.path) as string;
    const redirect = decodeURIComponent(redirectPath);
    const nextData = to.path === redirect ? { ...to, replace: true } : { path: redirect };
    asyncRouteStore.setDynamicAddedRoute(true);
    next(nextData);

    loadingBar && loadingBar.finish();
  });

  router.afterEach((to, _, failure) => {
    // handleKeepAlive(to);
    document.title = (to?.meta?.title as string) || document.title;
    if (isNavigationFailure(failure)) {
      //console.log('failed navigation', failure)
    }
    const asyncRouteStore = useAsyncRouteStoreWidthOut();
    // 在这里设置需要缓存的组件名称
    const keepAliveComponents = asyncRouteStore.keepAliveComponents;
    const currentComName: any = to.matched.find((item) => item.name == to.name)?.name;
    if (currentComName && !keepAliveComponents.includes(currentComName) && to.meta?.keepAlive) {
      // 需要缓存的组件
      keepAliveComponents.push(currentComName);
    } else if (!to.meta?.keepAlive || to.name == 'Redirect') {
      // 不需要缓存的组件
      const index = asyncRouteStore.keepAliveComponents.findIndex((name) => name == currentComName);
      if (index != -1) {
        keepAliveComponents.splice(index, 1);
      }
    }
    asyncRouteStore.setKeepAliveComponents(keepAliveComponents);
    loadingBar && loadingBar.finish();
  });

  /**
   * 递归处理多余的 layout : <router-view>
   */
  // function handleKeepAlive(to) {
  //   if (to.matched && to.matched.length > 2) {
  //     for (let i = 0; i < to.matched.length; i++) {
  //       const element = to.matched[i];
  //       if (element.components.default.name === 'Layout') {
  //         to.matched.splice(i, 1);
  //         handleKeepAlive(to);
  //       }
  //     }
  //   }
  // }
}
